import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import {MatProgressBar} from '@angular/material/progress-bar';
import {tap} from 'rxjs';
import {LoadingIndicatorService} from '../../service/loading-indicator.service';
import {RouteConfigLoadEnd, RouteConfigLoadStart, Router} from '@angular/router';
import {LoadingIndicatorEvent} from '../../event/loading-indicator.event';
import {LoadingIndicatorEventTypeEnum} from '../../enum/loading-indicator-event-type.enum';
import {NgIf} from '@angular/common';

/**
 * @class
 * @description This component is responsible for displaying a loading-indicator indicator.
 * @export
 * @memberof LoadingIndicatorComponent
 * @name LoadingIndicatorComponent
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatProgressBar
  ],
  selector: 'app-loading-indicator',
  standalone: true,
  styleUrl: './loading-indicator.component.scss',
  templateUrl: './loading-indicator.component.html'
})
export class LoadingIndicatorComponent implements OnInit, AfterViewInit {

  /**
   * @default 'none'
   * @description This property denotes the transition of the loading-indicator indicator.
   * @memberof LoadingIndicatorComponent
   * @name LoadingIndicatorComponent#transition
   * @property
   * @protected
   * @type {string}
   */
  protected transition: string = 'none';

  /**
   * @default false
   * @description This property denotes whether the loading-indicator indicator is visible or not.
   * @memberof LoadingIndicatorComponent
   * @name LoadingIndicatorComponent#visible
   * @property
   * @protected
   * @type {boolean}
   */
  protected visible: boolean = false;

  /**
   * @default true
   * @description This property denotes whether to detect route transitions or not.
   * @memberof LoadingIndicatorComponent
   * @name LoadingIndicatorComponent#detectRouteTransitions
   * @property
   * @public
   * @type {boolean}
   */
  @Input()
  public detectRouteTransitions: boolean = true;

  /**
   * @default 0
   * @description This property denotes the progress of the loading-indicator indicator.
   * @memberof LoadingIndicatorComponent
   * @name LoadingIndicatorComponent#progress
   * @property
   * @public
   * @type {number}
   */
  @Input()
  public progress: number = 0;

  /**
   * @constructor
   * @description This method instantiates a new instance of our loading-indicator component.
   * @memberof LoadingIndicatorComponent
   * @name LoadingIndicatorComponent#constructor
   * @param {ChangeDetectorRef} changeDetector The change detector reference.
   * @param {LoadingIndicatorService} loadingService The loading-indicator service.
   * @param {Router} router The router.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the change detector reference.
     * @memberof LoadingIndicatorComponent
     * @name LoadingIndicatorComponent#changeDetector
     * @private
     * @property
     * @readonly
     * @type {ChangeDetectorRef}
     */
    private readonly changeDetector: ChangeDetectorRef,
    /**
     * @description This property contains the loading-indicator service.
     * @memberof LoadingIndicatorComponent
     * @name LoadingIndicatorComponent#loadingService
     * @private
     * @property
     * @readonly
     * @type {LoadingIndicatorService}
     */
    private readonly loadingService: LoadingIndicatorService,
    /**
     * @description This property contains the router.
     * @memberof LoadingIndicatorComponent
     * @name LoadingIndicatorComponent#router
     * @private
     * @property
     * @readonly
     * @type {Router}
     */
    private readonly router: Router
  ) { }

  /**
   * @abstract
   * @description This method initializes the loading-indicator component after the view has been initialized.
   * @memberof LoadingIndicatorComponent
   * @method
   * @name LoadingIndicatorComponent#ngAfterViewInit
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Subscribe to the loading-indicator service events.
    this.loadingService.events.subscribe((e: LoadingIndicatorEvent): void => {

      // Check for a hidden event then hide the element.
      if (e.type === LoadingIndicatorEventTypeEnum.Hidden) this.visible = false;

      // Check for a shown event then show the element.
      if (e.type === LoadingIndicatorEventTypeEnum.Visible) this.visible = true;

      // Detect any changes that may have occurred.
      this.changeDetector.detectChanges();
    });
  }

  /**
   * @abstract
   * @description This method initializes the loading-indicator component.
   * @memberof LoadingIndicatorComponent
   * @method
   * @name LoadingIndicatorComponent#ngOnInit
   * @public
   * @returns {void}
   */
  public ngOnInit(): void {

    // Check to see if we need to bind to route transitions.
    if (this.detectRouteTransitions) this.router.events.pipe(tap({

      // Bind to the error event handler which simply turns the loading-indicator service off.
      error: (e): void => this.loadingService.error(e),

      // Bind to the next event handler.
      next: (e): void => {

        // Check for a route configuration loading-indicator-start event, then turn the loading-indicator service on.
        if (e instanceof RouteConfigLoadStart) this.loadingService.start();

        // Check for a route configuration loading-indicator-end event, then turn the loading-indicator service off.
        if (e instanceof RouteConfigLoadEnd) this.loadingService.stop();
      }
    })).subscribe();

    // Set the initial progress into the loading-indicator service.
    this.loadingService.progress(this.progress);

    // Subscribe to the loading-indicator service events.
    this.loadingService.events.subscribe((e: LoadingIndicatorEvent): void => {

      // Check for a hidden event then hide the element.
      if (e.type === LoadingIndicatorEventTypeEnum.Hidden) this.visible = false;

      // Check for a progress event then set the progress.
      if (e.type === LoadingIndicatorEventTypeEnum.Progress) {

        // Localize the progress value.
        const progress: number = e.value as number;

        // Set the transition value based on the progress value.
        this.transition = progress >= this.progress ? 'all 0.5s ease-in-out' : 'none';

        // Set the progress value into the component.
        this.progress = e.value as number;
      }

      // Check for a shown event then show the element.
      if (e.type === LoadingIndicatorEventTypeEnum.Visible) this.visible = true;

      // Detect any changes that may have occurred.
      this.changeDetector.detectChanges();
    });
  }
}
